import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faComments,
  faFileInvoiceDollar,
  faQuoteLeft,
  faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import { customerRequestThreadService } from '@/services/customer-request-thread.service';
import { CUSTOMER_PORTAL_ROUTES, customerRequestThreadPath } from '@/routes/customer-portal-routes';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerKeyValue } from '@/components/customer-portal/CustomerKeyValue';
import { CustomerStatusBanner } from '@/components/customer-portal/CustomerStatusBanner';
import type { CustomerBannerTone } from '@/components/customer-portal/CustomerStatusBanner';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerUnavailableState } from '@/components/customer-portal/CustomerUnavailableState';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerSkeletonDetail } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import type { CustomerBadgeTone } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerThreadTimeline } from './CustomerThreadTimeline';
import {
  CustomerThreadActions,
  type CustomerWorkOrderApprovalPayload,
} from './CustomerThreadActions';
import { customerModificationService } from '@/services/customer-modification.service';
import { toast } from '@/lib/swal';
import { isNotFoundError } from '@/lib/is-not-found-error';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import type {
  CustomerRequestThread,
  CustomerThreadStatus,
} from '@/types/customer-request-thread.types';

/**
 * Stage D-iii — the customer Request Thread WORKSPACE SHELL (D-145 / OD-D1).
 *
 * ── THE SERVER HAS ALREADY DECIDED EVERYTHING THAT MATTERS ──────────────────
 * This page maps the D-ii DTO onto a layout and does no reasoning of its own. It
 * does not compute a status (`status` arrives already collapsed from eleven
 * internal states to four), does not decide whether the customer owes a response
 * (`awaitingYourResponse` arrives derived), does not work out which request text
 * is current (`request.latest` arrives derived from customer-visible entries),
 * and does not check access — an inaccessible round is a 404 from the server, and
 * there is nothing here that could second-guess that. The one map below turns a
 * status into WORDS; it is not a second status mapper.
 *
 * ── WHAT THIS STAGE DELIBERATELY DOES NOT RENDER ────────────────────────────
 * The conversation itself and every action. `timeline` and `capabilities` are in
 * the response and are deliberately untouched here: rendering events is D-iv,
 * and Reply / Add / Approve / Reject / Request-modification are D-v. The two
 * reserved regions below are structural space with no controls in them — not
 * even disabled ones, which would promise something the page cannot yet do.
 *
 * ── ONE REQUEST, NO CLIENT FILTERING ────────────────────────────────────────
 * One call to the composed endpoint. Nothing is fetched and then filtered in the
 * browser; no entry, round, history or approval endpoint is called; the staff
 * thread is not reachable from this application branch at all.
 */

/**
 * Status → words. Keyed by the four values the server may send, and nothing
 * else: the internal state names never arrive here and could not be rendered.
 *
 * `UNKNOWN_STATUS` is the fail-safe. If a future server version sends a status
 * this build has not heard of, the page shows a neutral, truthful strip rather
 * than crashing or — worse — guessing a meaning.
 */
const STATUS_PRESENTATION: Record<
  CustomerThreadStatus,
  {
    tone: CustomerBannerTone;
    badge: string;
    badgeTone: CustomerBadgeTone;
    title: string;
    message: string;
  }
> = {
  UNDER_REVIEW: {
    tone: 'pending',
    badge: 'Under review',
    badgeTone: 'info',
    title: "We're reviewing your request",
    message: 'The service team is looking at what you asked for. Nothing is needed from you yet.',
  },
  INFORMATION_NEEDED: {
    tone: 'warning',
    badge: 'Information needed',
    badgeTone: 'warning',
    title: 'We need a little more information',
    message: 'The service team has a question about your request.',
  },
  QUOTATION_READY: {
    tone: 'warning',
    badge: 'Quotation ready',
    badgeTone: 'warning',
    title: 'An updated quotation is ready',
    message: 'The service team has sent an updated quotation for this request.',
  },
  CLOSED: {
    tone: 'info',
    badge: 'Closed',
    badgeTone: 'neutral',
    title: 'This request has been answered',
    message: 'The service team has responded and this request is now closed.',
  },
};

const UNKNOWN_STATUS = {
  tone: 'info' as CustomerBannerTone,
  badge: 'In progress',
  badgeTone: 'neutral' as CustomerBadgeTone,
  title: 'Your request',
  message: 'The service team is handling your request.',
};

/** Shown under the banner when the request is waiting on the customer. It says
 * THAT something is needed, never WHAT — which action applies is the server's
 * answer (`capabilities`) and D-v's to render. */
const AWAITING_NOTE = 'This request is waiting on you.';

function fmtDateTime(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Long words — a 60-character document name, an unbroken URL a customer typed
 * into their request — must wrap rather than widen the page. */
const PROSE = 'whitespace-pre-wrap break-words text-sm leading-6 text-customer-text';

export function CustomerRequestThreadPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();

  const { data, isPending, isError, error, refetch } = useQuery({
    // The round id is part of the key, so moving from one request to another
    // cannot show the previous request's content under the new URL: the new key
    // has no cached data and the page renders its loading state instead.
    queryKey: ['customer-portal', 'requests', roundId],
    queryFn: () => customerRequestThreadService.getByRoundId(roundId as string),
    enabled: !!roundId,
  });

  /** The way out of every dead end on this route. Deliberately the jobs list and
   * not the request's own job: offering a link to the job would confirm that the
   * job — and therefore the request — exists. */
  const backToJobs = (
    <CustomerButton
      variant="outline"
      icon={faArrowLeft}
      onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.JOBS)}
    >
      Back to Jobs
    </CustomerButton>
  );

  /**
   * One refused experience, for every reason.
   *
   * The server answers 404 for a request that does not exist, one belonging to
   * another customer, one in another tenant, and one outside this contact's
   * location scope — indistinguishably, which is the point (OD-D10). A 403 can
   * only come from the permission guard, before any round is looked up, and is
   * folded in here rather than given a message of its own: a separate
   * "you do not have access to this request" would describe the REQUEST, which
   * is exactly the sentence the design forbids. The copy below therefore names
   * no reason at all.
   */
  const refused = (
    <CustomerUnavailableState
      title="Request not available"
      description="This request either does not exist or is not available to your account."
      action={backToJobs}
    />
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      <button
        onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.JOBS)}
        className="customer-pressable -ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-customer-sm px-2 text-sm font-medium text-customer-text-secondary hover:bg-customer-text/[0.05] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" aria-hidden="true" />
        Back to Jobs
      </button>

      {/* `isPending` covers the first load AND a move to a different round id —
          in both cases there is no data for this key, so nothing stale can show. */}
      {isPending && (
        <div data-testid="customer-request-thread-loading">
          <CustomerSkeletonDetail />
        </div>
      )}

      {isError && !isPending && (isNotFoundError(error) || isForbiddenError(error)) && refused}

      {isError && !isPending && !isNotFoundError(error) && !isForbiddenError(error) && (
        // Never the server's message: a connection failure and a 500 read the
        // same to a customer, and neither may carry exception text.
        <CustomerErrorState
          message="We couldn't load this request. Please check your connection and try again."
          onRetry={() => void refetch()}
        />
      )}

      {data && !isPending && !isError && (
        <Workspace thread={data} refetch={() => refetch().then(() => undefined)} />
      )}
    </div>
  );
}

/**
 * What a failed action tells the customer.
 *
 * Never the server's own words. A 409 is the one case with a specific, useful
 * meaning — the quotation moved while they were reading — and it is the case
 * where the thread MUST be re-read before anything else is offered. Everything
 * else is one safe sentence: an exception message, a Prisma error or a stack
 * trace must never reach a customer's screen.
 */
function actionErrorMessage(status: number | undefined): string {
  if (status === 409) {
    return 'An updated quotation is now available, or this request has already been answered. The latest version is shown now.';
  }
  if (status === 404)
    return 'This request is no longer available. The latest version is shown now.';
  if (status === 400 || status === 422) {
    return 'Some of the details were not accepted. Please check them and try again.';
  }
  return "We couldn't send that. Please try again.";
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | undefined)?.response?.status;
}

function Workspace({
  thread,
  refetch,
}: {
  thread: CustomerRequestThread;
  refetch: () => Promise<void>;
}) {
  const presentation = STATUS_PRESENTATION[thread.status] ?? UNKNOWN_STATUS;
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  /**
   * Every action runs through here, so all five share one lifecycle.
   *
   * `submitting` is the duplicate-submit guard: it is set before the request and
   * cleared only after the refetch, so a second click during the round trip does
   * nothing. On the way out the thread is ALWAYS re-read — after a success
   * because the server has just created the entries and moved the state, and
   * after a failure because the failure usually means the round is no longer what
   * this page thought it was. Nothing is retried automatically: a decision is
   * destructive, and re-sending one the server just refused would be exactly the
   * wrong reflex.
   *
   * ── UAT-6 (Stage E-ii) — the one exception to "always re-read" ─────────────
   * `next` lets an action name where the customer belongs afterwards. It exists
   * for T16: REQUEST_MODIFICATION closes THIS round and opens a successor, so
   * re-reading the round in the URL would leave the customer looking at a thread
   * that is now closed. The successor's id comes from the decision response
   * itself (`successorRoundId`, added by E-i) — never from a second lookup, and
   * never derived from the chain.
   *
   * When a destination is produced the refetch is skipped deliberately: this
   * page is keyed by `roundId`, so the successor's route mounts and loads its
   * own thread. Re-reading the closed predecessor first would be a wasted
   * request against a component that is about to unmount. `next` is consulted
   * only after a success, so a failure still re-reads exactly as before.
   */
  async function run<T>(
    action: () => Promise<T>,
    success: string,
    next?: (result: T) => string | null,
  ): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    let destination: string | null = null;
    try {
      const result = await action();
      toast.success(success);
      destination = next?.(result) ?? null;
    } catch (error) {
      toast.error(actionErrorMessage(statusOf(error)));
    } finally {
      if (destination) {
        navigate(destination);
      } else {
        // The server's answer replaces every local assumption — including the
        // capabilities, which may now be different because of what just happened
        // or because somebody else moved the round.
        await refetch();
      }
      setSubmitting(false);
    }
  }

  const jobId = thread.job.id;
  const roundId = thread.roundId;
  // Present whenever a decision is offered: the server only sets a decision
  // capability when a sent revision is bound. Used as the decision's target, so
  // the decision names the revision the customer was actually shown — and the
  // server refuses it with 409 if a newer one arrived meanwhile.
  const revisionId = thread.quotation?.revisionId ?? '';

  // Shown as its own block only when it actually differs. When a customer never
  // added to their request the two are identical, and printing the same
  // paragraph twice under two headings implies a change that did not happen.
  const latest = thread.request.latest?.trim() ? thread.request.latest : null;
  const hasDistinctLatest = latest !== null && latest !== thread.request.original;

  const context = [
    { label: 'Job', value: thread.job.inspectionNumber },
    { label: 'Equipment', value: thread.job.equipmentName ?? '—' },
    {
      label: 'Request',
      value: `${thread.chain.requestNumber} of ${thread.chain.totalRequests}`,
    },
    { label: 'Opened', value: fmtDateTime(thread.openedAt) ?? '—' },
    ...(thread.closedAt ? [{ label: 'Closed', value: fmtDateTime(thread.closedAt) ?? '—' }] : []),
  ];

  return (
    <div className="customer-fade-in space-y-4 sm:space-y-5" data-testid="customer-request-thread">
      <CustomerPageHeader
        eyebrow="Your request"
        title={`Request ${thread.roundNumber}`}
        subtitle={`Job ${thread.job.inspectionNumber}`}
        actions={
          <CustomerStatusBadge label={presentation.badge} tone={presentation.badgeTone} icon />
        }
      />

      <CustomerStatusBanner
        data-testid="customer-request-thread-status"
        tone={presentation.tone}
        title={presentation.title}
        message={
          <>
            {presentation.message}
            {thread.awaitingYourResponse && (
              <span
                className="mt-1 block font-medium text-customer-text"
                data-testid="customer-request-thread-awaiting"
              >
                {AWAITING_NOTE}
              </span>
            )}
          </>
        }
      />

      <CustomerSectionCard title="This request" icon={faScrewdriverWrench} variant="flat">
        <CustomerKeyValue items={context} layout="grid" />
      </CustomerSectionCard>

      <CustomerSectionCard title="Your original request" icon={faQuoteLeft}>
        <p className={PROSE} data-testid="customer-request-thread-original">
          {thread.request.original}
        </p>
      </CustomerSectionCard>

      {hasDistinctLatest && (
        <CustomerSectionCard title="Latest update to your request" icon={faComments}>
          <p className={PROSE} data-testid="customer-request-thread-latest">
            {latest}
          </p>
        </CustomerSectionCard>
      )}

      {thread.quotation && (
        <CustomerSectionCard
          title="Quotation for this request"
          icon={faFileInvoiceDollar}
          variant="flat"
        >
          <CustomerKeyValue
            data-testid="customer-request-thread-quotation"
            layout="grid"
            items={[
              { label: 'Revision', value: `Revision ${thread.quotation.revisionNumber}` },
              { label: 'Reference', value: thread.quotation.quotationReference ?? '—' },
              { label: 'Document', value: thread.quotation.documentName ?? '—' },
              { label: 'Sent', value: fmtDate(thread.quotation.sentAt) ?? '—' },
            ]}
          />
        </CustomerSectionCard>
      )}

      {/* The conversation, exactly as the server sent it. The response also
          carries `capabilities`; it stays untouched — rendering an action is
          D-v's, and inspecting a capability here in order to decide what to show
          would put that decision in the browser. */}
      <CustomerSectionCard title="Conversation" icon={faComments}>
        <CustomerThreadTimeline events={thread.timeline} />
        <CustomerThreadActions
          capabilities={thread.capabilities}
          quotation={thread.quotation}
          submitting={submitting}
          handlers={{
            onReply: (message) =>
              run(
                () => customerModificationService.reply(jobId, roundId, message),
                'Your reply has been sent.',
              ),
            onAdd: (message) =>
              run(
                () => customerModificationService.addToRequest(jobId, roundId, message),
                'Added to your request.',
              ),
            onApprove: (payload: CustomerWorkOrderApprovalPayload) =>
              run(
                () =>
                  customerModificationService.decide(jobId, roundId, {
                    decision: 'APPROVE',
                    revisionId,
                    workOrderNumber: payload.workOrderNumber,
                    workOrderDescription: payload.workOrderDescription,
                    file: payload.file,
                  }),
                'Your approval and Work Order have been received.',
              ),
            onReject: (message) =>
              run(
                () =>
                  customerModificationService.decide(jobId, roundId, {
                    decision: 'REJECT',
                    revisionId,
                    message,
                  }),
                'Your response has been submitted.',
              ),
            onRequestModification: (requestText) =>
              run(
                () =>
                  customerModificationService.decide(jobId, roundId, {
                    decision: 'REQUEST_MODIFICATION',
                    revisionId,
                    requestText,
                  }),
                'Your request has been sent.',
                // UAT-6 — T16 closed this round and opened the next one. Follow
                // the customer to it, using the id the decision itself returned
                // and the canonical route helper. If the server somehow returns
                // no successor we stay put and re-read, rather than inventing an
                // id or guessing at one.
                (round) =>
                  round?.successorRoundId
                    ? customerRequestThreadPath(round.successorRoundId)
                    : null,
              ),
          }}
        />
      </CustomerSectionCard>
    </div>
  );
}
